/**
 * Money is integer paise everywhere in application code.
 *
 * Floating point rupees cannot represent 0.1 exactly, so every arithmetic step
 * here works on integers and rounds explicitly. Persistence converts to
 * `numeric(14,2)` at the edge (see `toDecimalString`), which Postgres stores
 * exactly; nothing in the domain ever adds two floats together.
 */
const isFiniteNumber = (v) => typeof v === 'number' && Number.isFinite(v);
/** Round half away from zero - the convention Indian invoicing uses. */
export function roundHalfUp(value) {
    return value < 0 ? -Math.round(-value) : Math.round(value);
}
/** ₹123.45 (number, string or Decimal-like) -> 12345 paise. */
export function toPaise(rupees) {
    const n = typeof rupees === 'number' ? rupees : Number(rupees.toString());
    if (!isFiniteNumber(n))
        throw new TypeError(`Not a money value: ${String(rupees)}`);
    return roundHalfUp(n * 100);
}
/** 12345 paise -> 123.45 (safe: exact for values under ₹90,000 crore). */
export function toRupees(paise) {
    return assertPaise(paise) / 100;
}
/** 12345 paise -> "123.45", the string form written to numeric(14,2). */
export function toDecimalString(paise) {
    const p = assertPaise(paise);
    const sign = p < 0 ? '-' : '';
    const abs = Math.abs(p);
    return `${sign}${Math.floor(abs / 100)}.${String(abs % 100).padStart(2, '0')}`;
}
export function assertPaise(value) {
    if (!Number.isInteger(value))
        throw new TypeError(`Paise must be an integer, got ${value}`);
    if (!Number.isSafeInteger(value))
        throw new RangeError(`Money value out of safe range: ${value}`);
    return value;
}
export const addMoney = (...values) => values.reduce((a, b) => a + assertPaise(b), 0);
export const subMoney = (a, b) => assertPaise(a) - assertPaise(b);
/** Unit price x quantity, where quantity may be fractional (0.35 kg). */
export const mulQty = (unitPrice, quantity) => roundHalfUp(assertPaise(unitPrice) * quantity);
/** A percentage of an amount, e.g. 5% GST or 12% PF. */
export const pctOf = (amount, percent) => roundHalfUp((assertPaise(amount) * percent) / 100);
/** Round to the nearest rupee - the round-off line on a tax invoice. */
export const toNearestRupee = (paise) => roundHalfUp(assertPaise(paise) / 100) * 100;
/** Round up to the nearest `step` rupees - used when suggesting menu prices. */
export function ceilToStep(paise, stepRupees) {
    const step = stepRupees * 100;
    return Math.ceil(assertPaise(paise) / step) * step;
}
/**
 * Split `total` across `weights` without losing or inventing a paisa.
 * Largest-remainder method: the biggest fractional parts absorb the rounding.
 */
export function allocate(total, weights) {
    assertPaise(total);
    const sum = weights.reduce((a, b) => a + b, 0);
    if (sum <= 0)
        return weights.map(() => 0);
    const exact = weights.map((w) => (total * w) / sum);
    const floors = exact.map((v) => Math.floor(v));
    let remainder = total - floors.reduce((a, b) => a + b, 0);
    const order = exact
        .map((v, i) => ({ i, frac: v - Math.floor(v) }))
        .sort((a, b) => b.frac - a.frac);
    const out = [...floors];
    for (const { i } of order) {
        if (remainder <= 0)
            break;
        out[i] = (out[i] ?? 0) + 1;
        remainder -= 1;
    }
    return out;
}
const inrFormatter = new Intl.NumberFormat('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
});
export const formatINR = (paise) => `₹${inrFormatter.format(toRupees(paise))}`;
export const clampNonNegative = (paise) => Math.max(0, assertPaise(paise));
//# sourceMappingURL=money.js.map